/** @jsxImportSource @emotion/react */
import {
  Button,
  createStyles,
  Errors,
  Input,
  List,
  ListItem,
  Select,
  Setters,
  Switch,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useParams } from 'react-router-dom';
import {
  BUSINESS_SECTORS,
  BusinessSector,
  KybFormData,
} from 'tabs/Merchants/types';
import { COUNTRY_OPTIONS } from 'utils';
import { ContactInformationForm } from './components/ContactInformationForm';
import { DateInput } from './components/DateInput';
import { RelatedPeople } from './components/RelatedPeople';

interface Props {
  formFields: KybFormData;
  formErrors: Errors<KybFormData>;
  formSetters: Setters<KybFormData>;
  editable: boolean;
  hasError: boolean;
  hasBeenEdited: boolean;
  loading: boolean;
  onSave: () => void;
}

export const KYBForm = ({
  formFields,
  formErrors,
  formSetters,
  editable,
  hasError,
  hasBeenEdited,
  loading,
  onSave,
}: Props) => {
  const styles = useStyles();
  const { merchantId } = useParams();
  const isNewMerchant = merchantId === 'new';
  return (
    <>
      <form css={[styles.container, styles.inputWrapper]}>
        <Input
          label="Contact email"
          value={formFields.contactEmail ?? ''}
          disabled={!editable}
          onChange={(e) => formSetters.contactEmail.set(e.target.value)}
          helperText={formErrors.contactEmail ?? undefined}
          error={!!formErrors.contactEmail}
        />
        <Input
          label="Settlement Report emails"
          value={formFields.settlementReportEmails ?? ''}
          disabled={!editable}
          onChange={(e) =>
            formSetters.settlementReportEmails.set(e.target.value)
          }
          helperText={
            formErrors.settlementReportEmails?.toString() ??
            'This will be used to send settlement report automatically when marked as processed. For multiple emails, separate them with a comma. For example: username1@domain1.com,username2@domain2.com'
          }
          error={!!formErrors.settlementReportEmails}
        />
        <Input
          label="Full company name"
          value={formFields.fullCompanyName}
          disabled={!editable}
          onChange={(e) => formSetters.fullCompanyName.set(e.target.value)}
          helperText={
            formErrors.fullCompanyName ??
            'This is the legal name of the merchant.'
          }
          error={!!formErrors.fullCompanyName}
        />
        <Input
          label="Previous company name(s)"
          value={formFields.previousCompanyNames ?? ''}
          disabled={!editable}
          onChange={(e) => formSetters.previousCompanyNames.set(e.target.value)}
        />
        <DateInput
          title="Incorporation date"
          value={formFields.incorporationDate}
          onChange={(e) => formSetters.incorporationDate.set(e)}
          disabled={!editable}
          error={!!formErrors.incorporationDate}
          helperText={formErrors.incorporationDate ?? ''}
        />
        <DateInput
          title="Onboarding date"
          value={formFields.onboardingDate}
          onChange={(e) => formSetters.onboardingDate.set(e)}
          disabled={!editable}
          error={!!formErrors.onboardingDate}
          helperText={formErrors.onboardingDate ?? ''}
        />

        <Select
          label="Incorporation country"
          options={COUNTRY_OPTIONS}
          values={
            formFields.incorporationCountry
              ? [formFields.incorporationCountry]
              : []
          }
          disabled={!editable}
          onSelect={([selected]) =>
            formSetters.incorporationCountry.set(selected)
          }
          error={!!formErrors.incorporationCountry}
          helperText={formErrors.incorporationCountry}
        />

        <Input
          label="Registration number"
          value={formFields.registrationNumber}
          disabled={!editable}
          onChange={(e) => formSetters.registrationNumber.set(e.target.value)}
          error={!!formErrors.registrationNumber}
          helperText={formErrors.registrationNumber}
        />
        <Input
          label="Country of tax residence"
          value={formFields.countryOfTaxResidence ?? ''}
          disabled={!editable}
          onChange={(e) =>
            formSetters.countryOfTaxResidence.set(
              e.target.value === '' ? null : e.target.value
            )
          }
          error={!!formErrors.countryOfTaxResidence}
          helperText={formErrors.countryOfTaxResidence ?? ''}
        />
        <Input
          label="Tax Identification Number"
          value={formFields.taxIdentificationNumber ?? ''}
          disabled={!editable}
          onChange={(e) =>
            formSetters.taxIdentificationNumber.set(
              e.target.value === '' ? null : e.target.value
            )
          }
          error={!!formErrors.taxIdentificationNumber}
          helperText={formErrors.taxIdentificationNumber ?? ''}
        />
        <Input
          label="LEI number"
          value={formFields.leiNumber ?? ''}
          disabled={!editable}
          onChange={(e) =>
            formSetters.leiNumber.set(
              e.target.value === '' ? null : e.target.value
            )
          }
          error={!!formErrors.leiNumber}
          helperText={formErrors.leiNumber ?? ''}
        />

        <Input
          label="Expected monthly volume"
          type="number"
          value={formFields.expectedMonthlyVolume.toString()}
          endAdornment="€"
          disabled={!editable}
          onChange={(e) =>
            formSetters.expectedMonthlyVolume.set(
              e.target.value ? parseInt(e.target.value) : 0
            )
          }
          error={!!formErrors.expectedMonthlyVolume}
          helperText={formErrors.expectedMonthlyVolume}
        />
        <Input
          label="Expected monthly transactions"
          type="number"
          value={formFields.expectedMonthlyTransactions.toString()}
          disabled={!editable}
          onChange={(e) =>
            formSetters.expectedMonthlyTransactions.set(
              e.target.value ? parseInt(e.target.value) : 0
            )
          }
          error={!!formErrors.expectedMonthlyTransactions}
          helperText={formErrors.expectedMonthlyTransactions}
        />
        <Input
          label="Last year's turnover"
          type="number"
          value={formFields.turnover.toString()}
          endAdornment="€"
          disabled={!editable}
          onChange={(e) =>
            formSetters.turnover.set(
              e.target.value ? parseInt(e.target.value) : 0
            )
          }
          error={!!formErrors.turnover}
          helperText={formErrors.turnover}
        />
        <Input
          label="License for business"
          value={formFields.licenseForBusiness ?? ''}
          disabled={!editable}
          onChange={(e) => formSetters.licenseForBusiness.set(e.target.value)}
        />
        <Input
          label="Customer jurisdictions"
          value={formFields.jurisdictions}
          disabled={!editable}
          onChange={(e) => formSetters.jurisdictions.set(e.target.value)}
          error={!!formErrors.jurisdictions}
          helperText={formErrors.jurisdictions}
        />
        <Input
          label="Business sectors"
          value={formFields.businessSectors.join(', ')}
          disabled={!editable}
          onChange={(e) => {
            if (!e.target.value) {
              formSetters.businessSectors.set([]);
              return;
            }
            formSetters.businessSectors.set(
              e.target.value.split(', ') as BusinessSector[]
            );
          }}
          endAdornment={
            <UtilityButton
              text="Options"
              dropdown={({ close }) => (
                <List css={styles.dropdownList}>
                  {Object.keys(BUSINESS_SECTORS).map((sector) => (
                    <ListItem
                      key={sector}
                      subtitle={sector}
                      onClick={() => {
                        const currentSectors = formFields.businessSectors;
                        if (
                          !currentSectors.includes(sector as BusinessSector)
                        ) {
                          formSetters.businessSectors.set([
                            ...currentSectors,
                            sector as BusinessSector,
                          ]);
                        }
                        close();
                      }}
                    />
                  ))}
                </List>
              )}
            />
          }
        />
        <Input
          label="Description of the company's business operations"
          type="textarea"
          rows={3}
          value={formFields.businessOperations ?? ''}
          onChange={(e) => formSetters.businessOperations.set(e.target.value)}
          error={!!formErrors.businessOperations}
          helperText={
            formErrors.businessOperations ??
            "Please add information about the company's source of funds"
          }
        />
        <Input
          label="What is the reason why your company wants to become a customer and use this specific product?"
          type="textarea"
          rows={3}
          value={formFields.reasonForUse ?? ''}
          onChange={(e) => {
            formSetters.reasonForUse.set(e.target.value);
          }}
          error={!!formErrors.reasonForUse}
          helperText={
            formErrors.reasonForUse ??
            'Please add information about the reason your company wants to become a customer and use this specific product'
          }
        />
        <Input
          label="Company's authorized signatory"
          value={formFields.authorizedSignatory ?? ''}
          onChange={(e) => formSetters.authorizedSignatory.set(e.target.value)}
          error={!!formErrors.authorizedSignatory}
          helperText={formErrors.authorizedSignatory ?? ''}
        />

        <ListItem
          title="Has USA connection/business"
          css={[styles.listItem, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={!!formFields.usaConnection}
              onChange={(checked) => formSetters.usaConnection.set(checked)}
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ContactInformationForm
          label="Registered address"
          data={formFields.registeredAddress}
          editable={editable}
          setters={formSetters.registeredAddress}
          errors={formErrors.registeredAddress}
        />

        <ContactInformationForm
          label="Primary address"
          data={formFields.primaryAddress}
          editable={editable}
          setters={formSetters.primaryAddress}
          errors={formErrors.primaryAddress}
        />

        <RelatedPeople
          data={formFields.relatedPeople}
          editable={editable}
          setters={formSetters.relatedPeople}
          errors={formErrors.relatedPeople}
        />
        <Input
          label="Risk score adjustment"
          type="number"
          value={
            formFields.riskScoreAdjustment
              ? formFields.riskScoreAdjustment.toString()
              : '0'
          }
          disabled={!editable}
          onChange={(e) => {
            formSetters.riskScoreAdjustment.set(
              e.target.value
                ? parseInt(e.target.value)
                : ('' as unknown as number)
            );
          }}
          error={!!formErrors.riskScoreAdjustment}
          helperText={formErrors.riskScoreAdjustment ?? ''}
        />
        <Input
          label="Risk score"
          type="number"
          value={
            formFields.riskScore === null ? '' : formFields.riskScore.toString()
          }
          placeholder="Not calculated"
          endAdornment={
            formFields.riskScore === null
              ? 'NOT CALCULATED'
              : formFields.riskScore >= 250
              ? 'UNACCEPTABLE'
              : formFields.riskScore >= 150
              ? 'HIGH'
              : 'LOW/MEDIUM'
          }
          disabled={true}
          onChange={(e) =>
            formSetters.riskScore.set(
              e.target.value ? parseInt(e.target.value) : null
            )
          }
        />
        <ListItem
          css={[styles.listItem, !editable && styles.listItemDisabled]}
          title="KYB is approved"
          action={
            <Switch
              checked={!!formFields.kybApproved}
              onChange={(checked) => formSetters.kybApproved.set(checked)}
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
        {!isNewMerchant && (
          <Button
            onClick={onSave}
            loading={loading}
            disabled={!editable || hasError || !hasBeenEdited || loading}
            text="Save"
          />
        )}
      </form>
    </>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  container: {
    background: palette.white,
    borderRadius: shape.borderRadius,
  },
  inputWrapper: {
    marginBottom: 'auto',
    padding: spacing.xxs,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing.xxs,
    border: `1px solid ${palette.grey.x100}`,
  },
  listItem: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
  listItemDisabled: {
    ':hover': {
      borderColor: 'inherit',
    },
  },
  dropdownList: {
    maxHeight: 300,
    overflowY: 'auto',
    background: palette.white,
    borderRadius: shape.borderRadius,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    minWidth: 300,
  },
}));
